import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  Divider,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  IconButton,
  Paper,
  Alert,
  Stack
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../utils/axios';
import {
  MARITAL_STATUS_OPTIONS,
  OFFICIATING_AUTHORITY_OPTIONS,
  BAPTIZED_OPTIONS,
  SACRAMENT_OPTIONS,
  INTENT_OPTIONS,
  MARITAL_NEEDS_MARRIAGE,
  MARITAL_NEEDS_PARTNER
} from '../utils/ociaFields';

const emptyAddress = () => ({ street: '', city: '', state: '', zip: '', country: '' });

const hasAddressContent = (addr) => !!addr && ['street', 'city', 'state', 'zip', 'country'].some(k => (addr[k] || '').trim());

// Statuses that get the "previously married" questions (widowed is intentionally excluded).
const NEEDS_PREV_MARRIAGE_QS = ['engaged', 'married', 'separated', 'divorced'];

const emptyForm = () => ({
  dateFilled: '',
  firstName: '', middleName: '', lastName: '', maidenName: '',
  dateOfBirth: '',
  placeOfBirth: { city: '', stateProvince: '', postalCode: '', country: '' },
  fatherName: '', motherName: '', motherMaidenName: '',
  isStudent: false,
  homeAddress: emptyAddress(),
  schoolAddress: emptyAddress(),
  cellPhone: '', occupation: '', email: '',
  religiousHistory: {
    presentAffiliation: '',
    everBaptized: '',
    baptism: { denomination: '', dateOrAge: '', baptismalName: '', churchName: '', churchAddress: '', location: '' },
    catholicSacramentsReceived: []
  },
  maritalStatus: {
    status: '',
    registrant: { marriedBefore: false, previousMarriages: [] },
    partner: { name: '', affiliation: '', marriedBefore: false, previousMarriages: [] },
    marriage: { dateOfMarriage: '', placeOfMarriage: '', officiatingAuthority: '' },
    widowedPreviousSpouseStillLiving: false
  },
  faithJourney: { whatLedYou: '', religiousEducationReceived: '', contactWithChurch: '', questionsConcerns: '' },
  intent: '',
  payment: { amount: 0, method: 'cash' },
  signature: { name: '', date: '' }
});

const toDateInput = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const normalizePreviousMarriages = (list) => (list || []).map(pm => ({
  name: pm.name || '',
  affiliation: pm.affiliation || '',
  dateOfMarriage: toDateInput(pm.dateOfMarriage),
  placeOfMarriage: pm.placeOfMarriage || '',
  officiatingAuthority: pm.officiatingAuthority || ''
}));

// Build the editable form state from an existing registration (deep, with safe defaults).
const fromRegistration = (reg) => {
  const base = emptyForm();
  if (!reg) return base;
  return {
    ...base,
    ...reg,
    dateFilled: toDateInput(reg.dateFilled),
    dateOfBirth: toDateInput(reg.dateOfBirth),
    placeOfBirth: { ...base.placeOfBirth, ...(reg.placeOfBirth || {}) },
    homeAddress: { ...base.homeAddress, ...(reg.homeAddress || {}) },
    schoolAddress: { ...base.schoolAddress, ...(reg.schoolAddress || {}) },
    religiousHistory: {
      ...base.religiousHistory,
      ...(reg.religiousHistory || {}),
      baptism: { ...base.religiousHistory.baptism, ...(reg.religiousHistory?.baptism || {}) },
      catholicSacramentsReceived: reg.religiousHistory?.catholicSacramentsReceived || []
    },
    maritalStatus: {
      ...base.maritalStatus,
      ...(reg.maritalStatus || {}),
      registrant: {
        marriedBefore: reg.maritalStatus?.registrant?.marriedBefore || false,
        previousMarriages: normalizePreviousMarriages(reg.maritalStatus?.registrant?.previousMarriages)
      },
      partner: {
        ...base.maritalStatus.partner,
        ...(reg.maritalStatus?.partner || {}),
        previousMarriages: normalizePreviousMarriages(reg.maritalStatus?.partner?.previousMarriages)
      },
      marriage: {
        dateOfMarriage: toDateInput(reg.maritalStatus?.marriage?.dateOfMarriage),
        placeOfMarriage: reg.maritalStatus?.marriage?.placeOfMarriage || '',
        officiatingAuthority: reg.maritalStatus?.marriage?.officiatingAuthority || ''
      }
    },
    faithJourney: { ...base.faithJourney, ...(reg.faithJourney || {}) },
    payment: { amount: reg.payment?.amount || 0, method: reg.payment?.method || 'cash' },
    signature: { name: reg.signature?.name || '', date: toDateInput(reg.signature?.date) }
  };
};

const AddressFields = ({ value, onChange }) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <TextField fullWidth label="Street" value={value.street} onChange={(e) => onChange('street', e.target.value)} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth label="City" value={value.city} onChange={(e) => onChange('city', e.target.value)} />
    </Grid>
    <Grid item xs={6} sm={3}>
      <TextField fullWidth label="State" value={value.state} onChange={(e) => onChange('state', e.target.value)} />
    </Grid>
    <Grid item xs={6} sm={3}>
      <TextField fullWidth label="Zip" value={value.zip} onChange={(e) => onChange('zip', e.target.value)} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth label="Country" value={value.country} onChange={(e) => onChange('country', e.target.value)} />
    </Grid>
  </Grid>
);

const SectionTitle = ({ children }) => (
  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>{children}</Typography>
);

const YesNoRadio = ({ label, value, onChange }) => (
  <FormControl>
    <FormLabel>{label}</FormLabel>
    <RadioGroup row value={value ? 'yes' : 'no'} onChange={(e) => onChange(e.target.value === 'yes')}>
      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
      <FormControlLabel value="no" control={<Radio />} label="No" />
    </RadioGroup>
  </FormControl>
);

const OCIAForm = ({ open, onClose, onSuccess, registration, schoolYear, currentSchoolYear }) => {
  const isEdit = Boolean(registration && registration._id);
  const [form, setForm] = useState(emptyForm());
  // UI-only: not saved — an on-campus student is represented by a filled schoolAddress.
  const [livesOnCampus, setLivesOnCampus] = useState(false);
  const [sendEmails, setSendEmails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(isEdit ? fromRegistration(registration) : emptyForm());
      setLivesOnCampus(isEdit ? hasAddressContent(registration?.schoolAddress) : false);
      setSendEmails(false);
      setError(null);
    }
  }, [open, registration, isEdit]);

  // Generic nested setter by dot-path.
  const setField = (path, value) => {
    setForm(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      keys.forEach((k, i) => {
        if (i === keys.length - 1) cur[k] = value;
        else { if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {}; cur = cur[k]; }
      });
      return next;
    });
  };

  const toggleSacrament = (value) => {
    setForm(prev => {
      const list = prev.religiousHistory.catholicSacramentsReceived || [];
      const next = list.includes(value) ? list.filter(v => v !== value) : [...list, value];
      return { ...prev, religiousHistory: { ...prev.religiousHistory, catholicSacramentsReceived: next } };
    });
  };

  // `party` is 'registrant' or 'partner' — both keep an identical previousMarriages list.
  const addPreviousMarriage = (party) => {
    setForm(prev => {
      const pm = prev.maritalStatus[party].previousMarriages || [];
      return {
        ...prev,
        maritalStatus: {
          ...prev.maritalStatus,
          [party]: {
            ...prev.maritalStatus[party],
            previousMarriages: [...pm, { name: '', affiliation: '', dateOfMarriage: '', placeOfMarriage: '', officiatingAuthority: '' }]
          }
        }
      };
    });
  };

  const updatePreviousMarriage = (party, index, key, value) => {
    setForm(prev => {
      const pm = [...(prev.maritalStatus[party].previousMarriages || [])];
      pm[index] = { ...pm[index], [key]: value };
      return {
        ...prev,
        maritalStatus: { ...prev.maritalStatus, [party]: { ...prev.maritalStatus[party], previousMarriages: pm } }
      };
    });
  };

  const removePreviousMarriage = (party, index) => {
    setForm(prev => {
      const pm = (prev.maritalStatus[party].previousMarriages || []).filter((_, i) => i !== index);
      return {
        ...prev,
        maritalStatus: { ...prev.maritalStatus, [party]: { ...prev.maritalStatus[party], previousMarriages: pm } }
      };
    });
  };

  // Repeatable previous-marriage cards, identical for the registrant and the spouse/fiancé(e).
  const renderPreviousMarriages = (party) => (
    <Box sx={{ mt: 1 }}>
      {(form.maritalStatus[party].previousMarriages || []).map((pm, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Previous Marriage {i + 1}</Typography>
            <IconButton size="small" color="error" onClick={() => removePreviousMarriage(party, i)}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Name" value={pm.name} onChange={(e) => updatePreviousMarriage(party, i, 'name', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Religious Affiliation" value={pm.affiliation} onChange={(e) => updatePreviousMarriage(party, i, 'affiliation', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth type="date" label="Date of Marriage" InputLabelProps={{ shrink: true }} value={pm.dateOfMarriage} onChange={(e) => updatePreviousMarriage(party, i, 'dateOfMarriage', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Place of Marriage" value={pm.placeOfMarriage} onChange={(e) => updatePreviousMarriage(party, i, 'placeOfMarriage', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Officiating Authority" value={pm.officiatingAuthority} onChange={(e) => updatePreviousMarriage(party, i, 'officiatingAuthority', e.target.value)}>
                {OFFICIATING_AUTHORITY_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      ))}
      <Button startIcon={<AddIcon />} onClick={() => addPreviousMarriage(party)} size="small">Add Previous Marriage</Button>
    </Box>
  );

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('First name, last name, and email are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...form,
        payment: { ...form.payment, amount: Number(form.payment.amount) || 0 }
      };
      if (isEdit) {
        await axios.put(`/ocia/${registration._id}`, payload);
      } else {
        payload.schoolYear = schoolYear || currentSchoolYear || undefined;
        payload.notifications = { sendEmails };
        await axios.post('/ocia', payload);
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save registration.');
    } finally {
      setSubmitting(false);
    }
  };

  const status = form.maritalStatus.status;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{isEdit ? 'Edit OCIA Registration' : 'New OCIA Registration'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <SectionTitle>Personal Information</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="Date Completed" InputLabelProps={{ shrink: true }}
              value={form.dateFilled} onChange={(e) => setField('dateFilled', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} />
          <Grid item xs={12} sm={4}>
            <TextField fullWidth required label="First Name" value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Middle Name" value={form.middleName} onChange={(e) => setField('middleName', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth required label="Last Name" value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Maiden Name (if applicable)" value={form.maidenName} onChange={(e) => setField('maidenName', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="Date of Birth" InputLabelProps={{ shrink: true }}
              value={form.dateOfBirth} onChange={(e) => setField('dateOfBirth', e.target.value)} />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Place of Birth</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}><TextField fullWidth label="City" value={form.placeOfBirth.city} onChange={(e) => setField('placeOfBirth.city', e.target.value)} /></Grid>
          <Grid item xs={12} sm={3}><TextField fullWidth label="State/Province" value={form.placeOfBirth.stateProvince} onChange={(e) => setField('placeOfBirth.stateProvince', e.target.value)} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth label="Postal Code" value={form.placeOfBirth.postalCode} onChange={(e) => setField('placeOfBirth.postalCode', e.target.value)} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth label="Country" value={form.placeOfBirth.country} onChange={(e) => setField('placeOfBirth.country', e.target.value)} /></Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Father's Name" value={form.fatherName} onChange={(e) => setField('fatherName', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Mother's Name" value={form.motherName} onChange={(e) => setField('motherName', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Mother's Maiden Name" value={form.motherMaidenName} onChange={(e) => setField('motherMaidenName', e.target.value)} /></Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Contact</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth required type="email" label="Email" value={form.email} onChange={(e) => setField('email', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Cell Phone" value={form.cellPhone} onChange={(e) => setField('cellPhone', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Occupation" value={form.occupation} onChange={(e) => setField('occupation', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}>
            <YesNoRadio
              label="Are you a student?"
              value={form.isStudent}
              onChange={(v) => {
                setField('isStudent', v);
                if (!v) {
                  setLivesOnCampus(false);
                  setField('schoolAddress', emptyAddress());
                }
              }}
            />
          </Grid>
          {form.isStudent && (
            <Grid item xs={12} sm={6}>
              <YesNoRadio
                label="Do you live on campus?"
                value={livesOnCampus}
                onChange={(v) => {
                  setLivesOnCampus(v);
                  if (!v) setField('schoolAddress', emptyAddress());
                }}
              />
            </Grid>
          )}
        </Grid>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Home Address</Typography>
        <AddressFields value={form.homeAddress} onChange={(k, v) => setField(`homeAddress.${k}`, v)} />
        {form.isStudent && livesOnCampus && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>School Address</Typography>
            <AddressFields value={form.schoolAddress} onChange={(k, v) => setField(`schoolAddress.${k}`, v)} />
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Religious History</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Present Religious Affiliation (if any)" value={form.religiousHistory.presentAffiliation}
              onChange={(e) => setField('religiousHistory.presentAffiliation', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Have you ever been baptized?" value={form.religiousHistory.everBaptized}
              onChange={(e) => setField('religiousHistory.everBaptized', e.target.value)}>
              {BAPTIZED_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>

        {form.religiousHistory.everBaptized === 'yes' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Baptism Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Denomination" value={form.religiousHistory.baptism.denomination} onChange={(e) => setField('religiousHistory.baptism.denomination', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Date or Approximate Age" value={form.religiousHistory.baptism.dateOrAge} onChange={(e) => setField('religiousHistory.baptism.dateOrAge', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Baptismal Name (if different)" value={form.religiousHistory.baptism.baptismalName} onChange={(e) => setField('religiousHistory.baptism.baptismalName', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Place of Baptism (Church Name)" value={form.religiousHistory.baptism.churchName} onChange={(e) => setField('religiousHistory.baptism.churchName', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Church Address (if known)" value={form.religiousHistory.baptism.churchAddress} onChange={(e) => setField('religiousHistory.baptism.churchAddress', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Location (if known)" value={form.religiousHistory.baptism.location} onChange={(e) => setField('religiousHistory.baptism.location', e.target.value)} /></Grid>
            </Grid>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>If baptized Catholic, sacraments already received:</Typography>
            <FormGroup row>
              {SACRAMENT_OPTIONS.map(o => (
                <FormControlLabel
                  key={o.value}
                  control={<Checkbox checked={form.religiousHistory.catholicSacramentsReceived.includes(o.value)} onChange={() => toggleSacrament(o.value)} />}
                  label={o.label}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Marital Status</SectionTitle>
        <TextField select fullWidth label="Marital Status" value={status}
          onChange={(e) => setField('maritalStatus.status', e.target.value)}>
          {MARITAL_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>

        {MARITAL_NEEDS_PARTNER.includes(status) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{status === 'engaged' ? 'Fiancé(e)' : 'Spouse'}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Name" value={form.maritalStatus.partner.name} onChange={(e) => setField('maritalStatus.partner.name', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Religious Affiliation" value={form.maritalStatus.partner.affiliation} onChange={(e) => setField('maritalStatus.partner.affiliation', e.target.value)} /></Grid>
            </Grid>
          </Box>
        )}

        {MARITAL_NEEDS_MARRIAGE.includes(status) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Marriage Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><TextField fullWidth type="date" label="Date of Marriage" InputLabelProps={{ shrink: true }} value={form.maritalStatus.marriage.dateOfMarriage} onChange={(e) => setField('maritalStatus.marriage.dateOfMarriage', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Place of Marriage" value={form.maritalStatus.marriage.placeOfMarriage} onChange={(e) => setField('maritalStatus.marriage.placeOfMarriage', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Officiating Authority" value={form.maritalStatus.marriage.officiatingAuthority} onChange={(e) => setField('maritalStatus.marriage.officiatingAuthority', e.target.value)}>
                  {OFFICIATING_AUTHORITY_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        )}

        {status === 'widowed' && (
          <Box sx={{ mt: 2 }}>
            <YesNoRadio
              label="Were you previously married to someone who is still living?"
              value={form.maritalStatus.widowedPreviousSpouseStillLiving}
              onChange={(v) => setField('maritalStatus.widowedPreviousSpouseStillLiving', v)}
            />
          </Box>
        )}

        {NEEDS_PREV_MARRIAGE_QS.includes(status) && (
          <Box sx={{ mt: 2 }}>
            <YesNoRadio
              label="Have you (the registrant) been previously married?"
              value={form.maritalStatus.registrant.marriedBefore}
              onChange={(v) => setField('maritalStatus.registrant.marriedBefore', v)}
            />
            {form.maritalStatus.registrant.marriedBefore && renderPreviousMarriages('registrant')}
            <Box sx={{ mt: 2 }}>
              <YesNoRadio
                label={status === 'engaged' ? 'Has the fiancé(e) been previously married?' : 'Has the spouse been previously married?'}
                value={form.maritalStatus.partner.marriedBefore}
                onChange={(v) => setField('maritalStatus.partner.marriedBefore', v)}
              />
              {form.maritalStatus.partner.marriedBefore && renderPreviousMarriages('partner')}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Faith Journey</SectionTitle>
        <Stack spacing={2}>
          <TextField fullWidth multiline minRows={2} label="What or who has led you to want to know more about the Catholic faith?" value={form.faithJourney.whatLedYou} onChange={(e) => setField('faithJourney.whatLedYou', e.target.value)} />
          <TextField fullWidth multiline minRows={2} label="Religious education you have received (as a child and as an adult)" value={form.faithJourney.religiousEducationReceived} onChange={(e) => setField('faithJourney.religiousEducationReceived', e.target.value)} />
          <TextField fullWidth multiline minRows={2} label="What contact have you had with the Catholic Church to date?" value={form.faithJourney.contactWithChurch} onChange={(e) => setField('faithJourney.contactWithChurch', e.target.value)} />
          <TextField fullWidth multiline minRows={2} label="Questions or concerns about the Catholic Church" value={form.faithJourney.questionsConcerns} onChange={(e) => setField('faithJourney.questionsConcerns', e.target.value)} />
          <TextField select fullWidth label="Present feelings about joining" value={form.intent} onChange={(e) => setField('intent', e.target.value)}>
            {INTENT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Payment & Signature</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Amount Due ($)" value={form.payment.amount} onChange={(e) => setField('payment.amount', e.target.value)} InputProps={{ inputProps: { min: 0 } }} /></Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Payment Method" value={form.payment.method} onChange={(e) => setField('payment.method', e.target.value)}>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="online">Online</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Signature Name" value={form.signature.name} onChange={(e) => setField('signature.name', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Signature Date" InputLabelProps={{ shrink: true }} value={form.signature.date} onChange={(e) => setField('signature.date', e.target.value)} /></Grid>
        </Grid>

        {!isEdit && (
          <FormControlLabel
            sx={{ mt: 2 }}
            control={<Checkbox checked={sendEmails} onChange={(e) => setSendEmails(e.target.checked)} />}
            label="Send confirmation email to registrant and notification to staff"
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Registration')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OCIAForm;
